import React from 'react'
import _ from 'lodash'
import { useImmer } from 'use-immer'
import { toast as displayToast } from 'react-hot-toast'
import Fernet from 'fernet-web'

// Component
import { Alert } from '../components/Alert'
import {
  oneUppercaseLetterRegEx,
  oneLowercaseLetterRegEx,
  oneNumberRegEx,
  oneSymbolRegEx,
} from '../components/PasswordVerifier'

// Utils & Styles
import { INCLUSION_OPTIONS, MULTI_VALUE_MERGE_FIELDS, OPERATOR_OPTIONS } from './constants'

const ERROR_DEFAULT = 'An error occurred. Please try again.'
const ERROR_NO_INTERNET = 'Request could not be made. Please check your internet connection.'
const ERROR_TIMEOUT = 'Request timed out. Please try again.'

export const createWithDoc = ({ envName = '', docFunction = () => {}, component = '' }) => {
  let createComponentWithDoc
  if (envName !== 'production') {
    createComponentWithDoc = docFunction(component) // eslint-disable-line global-require
  }
  return createComponentWithDoc || component
}

/**
 * Collects every message string from a DRF error value, however deeply it is nested.
 * - Nested serializers return `{ field: [{}, { value: ['Not a valid string.'] }] }`, one entry
 *   per item, so the messages can sit several levels down.
 * - Repeated messages (one per failing item) are only returned once.
 * @param {*} value
 * @returns {string[]}
 */
const flattenErrorMessages = (value) => {
  const collect = (v) => {
    if (_.isNil(v) || v === '') return []
    if (_.isString(v)) return [v]
    if (_.isArray(v)) return _.flatMap(v, collect)
    if (_.isPlainObject(v)) return _.flatMap(Object.values(v), collect)
    return [String(v)]
  }

  return _.uniq(collect(value))
}

/**
 * Returns a plain-text message for a failed request, safe to render.
 * @param {object} err
 * @returns {string}
 */
export const getErrorMessage = (err) => {
  // - Axios does not return a response object if the service cannot be reached
  if (err.code === 'ERR_NETWORK') {
    return ERROR_NO_INTERNET
  }

  if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
    return ERROR_TIMEOUT
  }

  // - If we do have a response and data object, use the first field's error(s). A string body is
  //   an HTML error page or similar, not a message meant for the user.
  if (_.isPlainObject(err.response?.data) || _.isArray(err.response?.data)) {
    const messages = flattenErrorMessages(Object.values(err.response.data)[0])
    if (messages.length > 0) return messages.join(' ')
  }

  // If a message has not been set, fallback to our default message
  return ERROR_DEFAULT
}

/**
 * Updates the state object with the updated field.
 * @param {object} state
 * @param {object} updatedField
 */
export const updateState = (state, updatedField) => ({
  ...state,
  ...updatedField,
})

/**
 * Updates an array stored in state.
 * - `add`: adds value to the array
 * - `remote`: removes the value at the index from the array
 * - `update`: updates the value at the index in the array
 * - `reset` and `filter`: returns the supplied value
 * - `clear`: removes all values from the array
 * @param {object} state
 * @param {object} updatedState
 */
export const updateArrayState = (state, { type, value, index }) => {
  switch (type) {
    case 'add':
      return [...state, value]
    case 'remove': {
      const updatedState = [...state]
      updatedState.splice(index, 1)
      return updatedState
    }
    case 'update': {
      const updatedState = [...state]
      updatedState[index] = value
      return updatedState
    }
    case 'reset':
    case 'filter':
      return value
    case 'clear':
      return []
    default:
      return state
  }
}

/**
 * Serializes a value with object keys in sorted order.
 *
 * For answering "has this changed since it was saved?" about data that makes a
 * round trip through the API. Plain `JSON.stringify` cannot answer it: values
 * stored in a Postgres `jsonb` column come back with their keys in a different
 * order, which compares as a difference even though nothing changed. A
 * canonical form compares the data rather than its spelling.
 *
 * @param {*} value
 * @returns {string}
 */
export const canonicalJson = (value) => {
  if (_.isArray(value)) return `[${_.map(value, canonicalJson).join(',')}]`

  if (_.isPlainObject(value)) {
    const entries = _.map(
      _.keys(value).sort(),
      (key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`,
    )
    return `{${entries.join(',')}}`
  }

  // `undefined` has no JSON form; treat it as absent rather than crashing.
  return JSON.stringify(value) ?? 'null'
}

export const joinClassNames = (...classes) => classes.filter(Boolean).join(' ')

export const useImmerState = useImmer

export const toast = (message, type) => {
  // An object or array here would crash the whole app on render, so reduce it to text first
  const text = _.isString(message)
    ? message
    : flattenErrorMessages(message).join(' ') || ERROR_DEFAULT

  return displayToast.custom(
    (t) =>
      t.visible && <Alert message={text} type={type} onClose={() => displayToast.dismiss(t.id)} />,
    // The id de-duplicates identical messages
    { id: text, duration: 4000 },
  )
}

/**
 * Handles updating pagination based on the supplied parameters and functions.
 * @param {number} page
 * @param {number} currentPage
 * @param {number} perPage
 * @param {number} totalRows
 * @param {object} pages
 * @param {func} setCurrentPage
 * @param {func} request
 * @param {string} baseUrl
 * @param {string} filter
 */
export const handlePagination = async (
  page,
  currentPage,
  perPage,
  totalRows,
  pages,
  setCurrentPage,
  request,
  baseUrl,
  filter = null,
) => {
  // If the user is requesting the first page and we are not on the next page,
  // we need to get the very first page and not utilize `previous`.
  if (page === 1 && currentPage > 1) {
    await request(`${baseUrl}${perPage}&${filter ? `${filter}&` : ''}page=1`)
  }
  // If the user is requesting the last page and we are not on the previous page,
  // we need to get the very last page and not utilize `next`.
  else if (page > currentPage && page - currentPage > 1) {
    await request(
      `${baseUrl}${perPage}&${filter ? `${filter}&` : ''}page=${Math.ceil(totalRows / perPage)}`,
    )
  }
  // If the user is requesting the next page.
  else if (page > currentPage) {
    await request(pages.next)
  }
  // Otherwise, the user is requesting the previous page.
  else {
    await request(pages.previous)
  }

  setCurrentPage(page)
}

/**
 * Handles verifying the password against requirements.
 * @param {string} value
 * @returns {string} error if there is one to display
 */
export const verifyPassword = (value) => {
  if (!value || value.length < 8) return 'Please enter more than 8 characters'
  if (!oneUppercaseLetterRegEx.test(value)) return 'Please enter at least 1 uppercase letter'
  if (!oneLowercaseLetterRegEx.test(value)) return 'Please enter at least 1 lowercase letter'
  if (!oneNumberRegEx.test(value)) return 'Please enter at least 1 number'
  if (!oneSymbolRegEx.test(value)) return 'Please enter at least 1 symbol'
  return undefined
}

/**
 * Sourced from: https://github.com/granteagon/move
 *
 * Moves an item in an array at the specified index to another position in the array.
 * @param {array} array
 * @param {number} moveIndex
 * @param {number} toIndex
 * @returns updated array
 */
export const move = (array, moveIndex, toIndex) => {
  /* #move - Moves an array item from one position in an array to another.

     Note: This is a pure function so a new array will be returned, instead
     of altering the array argument.

    Arguments:
    1. array     (String) : Array in which to move an item.         (required)
    2. moveIndex (Object) : The index of the item to move.          (required)
    3. toIndex   (Object) : The index to move item at moveIndex to. (required)
  */
  const item = array[moveIndex]
  const { length } = array
  const diff = moveIndex - toIndex

  if (diff > 0) {
    // move left
    return [
      ...array.slice(0, toIndex),
      item,
      ...array.slice(toIndex, moveIndex),
      ...array.slice(moveIndex + 1, length),
    ]
  }

  if (diff < 0) {
    // move right
    const targetIndex = toIndex + 1
    return [
      ...array.slice(0, moveIndex),
      ...array.slice(moveIndex + 1, targetIndex),
      item,
      ...array.slice(targetIndex, length),
    ]
  }
  return array
}

export const decrypt = async (data) => {
  const fernet = await Fernet.create(import.meta.env.VITE_FERNET_SECRET)
  const decrypted = await fernet.decrypt(data)
  return decrypted.toString()
}

/**
 * Handles configuring the complete list of merge field options.
 * @param {array} enabledMergeFields
 * @param {object} mergeFieldConfigs
 * @param {object} admissionItems
 * @param {object} categories
 * @param {object} sessions
 * @param {boolean} includeCventOnlyFields - includes merge fields that are only pulled
 * over for Cvent events.
 * @param {object} oilTypeOptions - OIL type options for Nimble events.
 */
export const configureMergeFieldOptions = (
  enabledMergeFields,
  mergeFieldConfigs,
  categories = null,
  admissionItems = null,
  sessions = null,
  includeCventOnlyFields = false,
  oilTypeOptions = null,
) => {
  const options = _.map(
    // Filter out `categoryName` since we use the id for values
    _.filter(enabledMergeFields, (f) => f !== 'categoryName'),
    (f) => {
      const matchingCustomFieldId = _.find(
        _.keys(mergeFieldConfigs),
        (o) => mergeFieldConfigs[o].text === f,
      )
      return { label: f, id: matchingCustomFieldId || f, baseField: !matchingCustomFieldId }
    },
  )

  if (categories) {
    options.unshift({ label: 'Category', id: 'categoryId', baseField: true })
  }
  if (admissionItems) {
    options.unshift({ label: 'Admission Item', id: 'admissionItem', baseField: true })
  }
  if (sessions) {
    options.unshift({ label: 'Sessions', id: 'sessions', baseField: true })
  }
  if (oilTypeOptions && !_.isEmpty(oilTypeOptions)) {
    // The OIL product names share the admission item options: a Nimble sync fills
    // `admission_items` with every OIL product name.
    if (admissionItems && !_.isEmpty(admissionItems)) {
      options.unshift({ label: 'OIL Product Name', id: 'oil_product_names', baseField: true })
    }
    options.unshift({ label: 'OIL Type', id: 'oil_types', baseField: true })
  }
  if (includeCventOnlyFields) {
    options.unshift({ label: 'Discount Codes', id: 'discount_codes', baseField: false })
    options.unshift({ label: 'Nickname', id: 'nickname', baseField: false })
  }

  return options
}

/**
 * Handles configuring the list of options possible for the selected merge field (if there are options).
 * @param {string} mergeField
 * @param {object} availableCustomFields
 * @param {object} categories
 * @param {object} admissionItems
 * @param {object} sessions
 */
export const configureSelectedMergeFieldOptions = (
  mergeField,
  availableCustomFields = null,
  categories = null,
  admissionItems = null,
  sessions = null,
  oilTypeOptions = null,
) => {
  if (!mergeField) return null
  let options = null

  // Manually configure options for admission items, sessions and categories since these are
  // base event objects rather than custom fields.
  if (
    mergeField.id === 'admissionItem' ||
    mergeField.id === 'oil_product_names' ||
    mergeField.id === 'sessions'
  ) {
    const data = mergeField.id === 'sessions' ? sessions : admissionItems
    options = _.map(_.keys(data), (k) => ({
      id: k,
      label: data[k].name,
    }))
  } else if (mergeField.id === 'categoryId') {
    options = _.map(_.keys(categories), (k) => ({
      id: k,
      label: categories[k],
    }))
  } else if (mergeField.id === 'oil_types' && oilTypeOptions) {
    options = _.map(_.keys(oilTypeOptions), (k) => ({
      id: k,
      label: oilTypeOptions[k].name,
    }))
  }
  // Only return custom field options if there are options set for that field
  else if (
    availableCustomFields &&
    availableCustomFields[mergeField?.id] &&
    availableCustomFields[mergeField?.id].options
  ) {
    options = _.map(availableCustomFields[mergeField?.id].options, (o) => ({
      id: o,
      label: o,
    }))
  }

  return options
}

/**
 * Returns the value to store for one logic rule.
 * - A picker value is an option, `{ id, label }`; the rule stores its id, even a falsy one.
 * - A free-text value is already a string.
 * - A populated / not populated rule stores no value, whatever the field holds.
 * @param {object} rule - A rule as the logic editor holds it, with `operator` as an option.
 * @returns {string|null}
 */
export const getLogicRuleValue = ({ operator, value }) => {
  const operatorId = _.isPlainObject(operator) ? operator.id : operator
  if (operatorId === 'populated' || operatorId === 'not_populated') return null
  if (_.isPlainObject(value)) return value.id ?? null
  return value ?? null
}

/**
 * Handles mapping the logic values to their respective options.
 * @param {array} logic
 * @param {array} mergeFieldOptions
 * @param {object} mergeFieldConfigs
 * @param {object} categories
 * @param {object} admissionItems
 * @param {object} sessions
 * @param {object} oilTypeOptions
 */
export const mapLogicValues = (
  logic,
  mergeFieldOptions,
  mergeFieldConfigs = null,
  categories = null,
  admissionItems = null,
  sessions = null,
  oilTypeOptions = null,
) =>
  _.map(logic, (l) => {
    const updatedLogic = { ...l }

    let { value } = updatedLogic
    if (updatedLogic.mergeField === 'admission_item') {
      updatedLogic.mergeField = 'admissionItem'
    }

    // Wrap values in { id, label } when the editor will render a picker for
    // them. Scalar fields use free-text TextInput for contains/not_contains
    // and need the raw string value preserved; multi-value fields like
    // sessions use the picker even for contains, so we wrap unconditionally.
    const isContainsOp =
      updatedLogic.operator === 'contains' || updatedLogic.operator === 'not_contains'
    const isMultiValueField = MULTI_VALUE_MERGE_FIELDS.includes(updatedLogic.mergeField)
    // A populated / not populated rule has no value. Wrapping its null gave
    // `{ id: null, label: null }`, which the next save sent as the value.
    const shouldWrap = !_.isNil(updatedLogic.value) && (!isContainsOp || isMultiValueField)

    if (
      shouldWrap &&
      mergeFieldConfigs &&
      mergeFieldConfigs[updatedLogic.mergeField]?.options &&
      mergeFieldConfigs[updatedLogic.mergeField]?.options.length > 0
    ) {
      value = { id: updatedLogic.value, label: updatedLogic.value }
    } else if (
      shouldWrap &&
      categories &&
      updatedLogic.mergeField === 'categoryId' &&
      categories[updatedLogic.value]
    ) {
      value = { id: updatedLogic.value, label: categories[updatedLogic.value] }
    } else if (
      shouldWrap &&
      admissionItems &&
      (updatedLogic.mergeField === 'admissionItem' ||
        updatedLogic.mergeField === 'oil_product_names') &&
      admissionItems[updatedLogic.value]
    ) {
      value = { id: updatedLogic.value, label: admissionItems[updatedLogic.value].name }
    } else if (
      shouldWrap &&
      sessions &&
      updatedLogic.mergeField === 'sessions' &&
      sessions[updatedLogic.value]
    ) {
      value = { id: updatedLogic.value, label: sessions[updatedLogic.value].name }
    } else if (
      shouldWrap &&
      oilTypeOptions &&
      updatedLogic.mergeField === 'oil_types' &&
      oilTypeOptions[updatedLogic.value]
    ) {
      value = { id: updatedLogic.value, label: oilTypeOptions[updatedLogic.value].name }
    }

    return {
      ...updatedLogic,
      inclusionLogic: INCLUSION_OPTIONS.find((o) => o.id === updatedLogic.inclusionLogic),
      mergeField: mergeFieldOptions.find((o) => o.id === updatedLogic.mergeField),
      operator: _.find(OPERATOR_OPTIONS, (o) => o.id === updatedLogic.operator),
      value,
    }
  })

/**
 * Handles determining if the logic is valid based on the attendee's data.
 * @param {array} visibilityLogic
 * @param {object} attendee
 */
const decodeHtmlEntities = (str) => {
  if (typeof str !== 'string') return str
  const textarea = document.createElement('textarea')
  textarea.innerHTML = str
  return textarea.value
}

// Coerce a potentially complex value (object, date, primitive) into a string
// suitable for comparison. Prefers human-readable fields (label/name) when
// dealing with structured attendee data like sessions/admission items.
const toComparable = (v) => {
  if (v === null || v === undefined) return ''
  if (v instanceof Date) return v.toISOString()
  if (typeof v === 'object') {
    const readable = v.label ?? v.name ?? v.value ?? v.id
    if (readable !== undefined && readable !== null && typeof readable !== 'object') {
      return String(readable)
    }
    return ''
  }
  return String(v)
}

// A value is "populated" when it has meaningful, non-whitespace content.
// Empty strings, whitespace-only strings, empty arrays, and objects with no
// readable label/name/value count as NOT populated.
const isPopulatedValue = (val) => {
  if (val === null || val === undefined) return false
  if (_.isArray(val)) return val.some((item) => isPopulatedValue(item))
  if (typeof val === 'object') return toComparable(val).trim() !== ''
  return String(val).trim() !== ''
}

// Map of merge field name to the lookups[] key holding {id: label} or
// {id: { name }} for that field. Used by `contains` to substring-match
// against the human-readable label rather than the stored option id.
const OPTION_BACKED_LOOKUPS = {
  categoryId: 'categories',
  admissionItem: 'admissionItems',
  sessions: 'sessions',
  oil_types: 'oilTypes',
}

// Resolve an attendee value (a stored option id) to its display label so
// that `contains`/`not_contains` can match against the label the user typed.
// `categories[id]` is a flat label string; the others are objects with `.name`.
// Falls through to the raw `val` when no lookup is available; downstream
// comparison logic applies `toComparable(...)`, preserving the pre-fix
// UUID-substring behavior for callers without lookups.
const resolveOptionLabel = (mergeField, val, lookups) => {
  const lookupKey = OPTION_BACKED_LOOKUPS[mergeField]
  if (!lookupKey || !lookups?.[lookupKey]) return val
  const table = lookups[lookupKey]

  const resolveOne = (item) => {
    if (item === null || item === undefined) return ''
    const id = typeof item === 'object' ? item?.id ?? toComparable(item) : item
    const entry = table[id]
    if (entry === undefined || entry === null) return toComparable(item)
    if (typeof entry === 'string') return entry
    return entry.name ?? entry.label ?? toComparable(item)
  }

  if (_.isArray(val)) return val.map(resolveOne)
  return resolveOne(val)
}

/**
 * Resolves a multi-value attendee field into the list of labels to print.
 *
 * `attendee.sessions` (and other option-backed fields) hold arrays of stored
 * option ids; printing those raw would put UUIDs on a badge. This resolves each
 * id through the same lookup tables `validateLogic` uses, drops anything that
 * resolves empty, and always returns an array — a scalar field yields a
 * single-element list rather than an error, so a badge list can repeat over any
 * field an author picks.
 *
 * Unlike the comparison helpers, an option-backed id with no matching option is
 * dropped rather than falling through to the raw value. Matching loosely is the
 * right call when deciding whether a rule passes; printing a bare id onto a
 * badge is not.
 *
 * The base attendee field is tried first and `customData` second, the same two
 * places `validateLogic` reads. The fallback matters for sessions: the API
 * attendee's `sessions` holds internal session ids, which the sessions table
 * (keyed by the registration provider's ids) never matches, while
 * `customData.sessions` holds the provider's ids. The kiosk attendee carries
 * only the latter, so without the fallback the preview prints nothing where the
 * kiosk prints the attendee's sessions.
 *
 * @param {string} mergeField accessor, e.g. 'sessions'
 * @param {object} attendee
 * @param {object} lookups { categories, admissionItems, sessions, oilTypes }
 * @returns {Array} display labels, in the attendee's own order
 */
export const resolveMultiValueLabels = (mergeField, attendee, lookups = {}) => {
  // Only option-backed fields have a table to miss. Free-text and custom
  // multi-select values carry their own labels and pass straight through.
  const lookupKey = OPTION_BACKED_LOOKUPS[mergeField]
  const table = lookupKey ? lookups?.[lookupKey] : null

  const resolveLabels = (raw) => {
    if (raw === null || raw === undefined) return []

    const values = _.isArray(raw) ? raw : [raw]

    return _.filter(
      _.map(values, (value) => {
        if (table) {
          const id = typeof value === 'object' ? value?.id : value
          if (table[id] === undefined || table[id] === null) return ''
        }

        return toComparable(resolveOptionLabel(mergeField, value, lookups)).trim()
      }),
      (label) => label !== '',
    )
  }

  const baseLabels = resolveLabels(attendee?.[mergeField])
  if (baseLabels.length > 0) return baseLabels

  return resolveLabels(attendee?.customData?.[mergeField])
}

export const validateLogic = (visibilityLogic, attendee, lookups = {}) => {
  if (!visibilityLogic || visibilityLogic.length === 0) return true

  const testArray = visibilityLogic.map((logic) => {
    const baseValue = attendee?.[logic.mergeField]
    const customValue = attendee?.customData?.[logic.mergeField]

    if (logic.operator === 'is' || logic.operator === 'is_not') {
      const configValue = logic.value ?? ''
      // Use _.toLower (not _.lowerCase) so leading/trailing spaces in the
      // configured value are preserved for exact matching.
      const configLower = _.toLower(configValue)
      const isNoneCheck = configLower === 'none'
      // The array branch is intentionally defensive: `is` is a scalar
      // operator and the editor steers multi-value fields toward `contains`
      // instead. Trackr's verifyAccess does NOT array-branch here, so
      // anything authored as `Sessions is X` will silently fail at the
      // door — keep using `Sessions contains X` for portability.
      const checkIs = (val) => {
        if (_.isArray(val)) {
          return val.some(
            (item) => _.toLower(decodeHtmlEntities(toComparable(item))) === configLower,
          )
        }
        if (isNoneCheck && !isPopulatedValue(val)) return true
        return _.toLower(decodeHtmlEntities(toComparable(val))) === configLower
      }
      const result = checkIs(baseValue) || checkIs(customValue)
      return logic.operator === 'is_not' ? !result : result
    }
    if (logic.operator === 'contains' || logic.operator === 'not_contains') {
      // Preserve leading/trailing spaces in search value — e.g. contains " member"
      // matches "CHI member" but not "non-member".
      const searchValue = _.toLower(logic.value ?? '')
      // When the configured value is itself a known option id (i.e. picked
      // from a dropdown rather than typed), bypass label resolution and
      // substring against raw ids. This is what makes
      // `Sessions contains <picked-session>` evaluate as set membership on
      // the attendee's array of session ids — matching Trackr's behavior.
      const lookupKey = OPTION_BACKED_LOOKUPS[logic.mergeField]
      const lookupTable = lookupKey ? lookups?.[lookupKey] : null
      const isPickerValue =
        !!lookupTable && logic.value != null && lookupTable[logic.value] !== undefined
      const checkContains = (val) => {
        // For free-text contains, resolve option ids to labels so
        // `Category contains "Sponsor"` matches attendees whose categoryId
        // resolves to "Exhibitor/Sponsor Member".
        const resolved = isPickerValue ? val : resolveOptionLabel(logic.mergeField, val, lookups)
        if (_.isArray(resolved)) {
          return resolved.some((item) =>
            _.toLower(decodeHtmlEntities(toComparable(item))).includes(searchValue),
          )
        }
        return _.toLower(decodeHtmlEntities(toComparable(resolved))).includes(searchValue)
      }
      const result = checkContains(baseValue) || checkContains(customValue)
      return logic.operator === 'not_contains' ? !result : result
    }
    if (logic.operator === 'populated' || logic.operator === 'not_populated') {
      const result = isPopulatedValue(baseValue) || isPopulatedValue(customValue)
      return logic.operator === 'not_populated' ? !result : result
    }
    return false
  })

  // The UI stores `inclusionLogic` on every rule. Historically only the
  // non-first rules reliably carried the chosen operator, so fall back to
  // checking any rule for OR.
  const isOr = _.some(visibilityLogic, (l) => l.inclusionLogic === 'OR')
  if (isOr) return testArray.includes(true)
  return testArray.every((test) => test)
}
